import * as Sentry from '@sentry/vue';
import { get } from 'lodash-es';
import api from '@/api/index.ts';
import { authService } from '@/plugins/vue-auth.ts';
import { updateLocalStorage } from '@/store/hooks/useLocalStoreContext.ts';

import defaultLexiconFr from '@/locales/fr-CA/lexicon/default.js';
import defaultLexiconEn from '@/locales/en-CA/lexicon/default.js';
import polestarLexiconFr from '@/locales/fr-CA/lexicon/polestar.js';
import polestarLexiconEn from '@/locales/en-CA/lexicon/polestar.js';
import hyundaiLexiconFr from '@/locales/fr-CA/lexicon/hyundai.js';
import hyundaiLexiconEn from '@/locales/en-CA/lexicon/hyundai.js';

// Plugins
import { wait } from '@/plugins/vue-wait.ts';
import i18n from '@/plugins/vue-i18n.js';
import Axios from 'axios';
import axios from '@/plugins/axios.js';
import { ls } from '@/plugins/vue-ls.ts';
import router from '@/router/index.ts';
import { START_LOCATION } from 'vue-router';

import { useAccountStore } from '@/store/modules/account/store.js';
import { useUserStore } from '@/store/modules/user/store.js';
import { useGlobalStore } from '@/store/store.ts';
import { parseQuery } from '@/utils/navigation.ts';
import { SearchBar } from '@/entities/SearchBar.ts';

// Utils
import { showLeadDeleted, showLeadNotFound } from '@/utils/toastr.js';
import { isValidNumber } from '@/utils/numbers.js';

// Entities
import Account from '@/entities/Account.js';
import Group from '@/entities/Group.js';
import Lead from '@/entities/Lead.js';
import User from '@/entities/User.js';

const getCurrentRoute = () => router.currentRoute.value;
const getContextRoute = () => {
    const committedRoute = getCurrentRoute();
    // Auth guards can still be resolving when app context initialization starts.
    return committedRoute === START_LOCATION
        ? router.resolve(`${globalThis.location.pathname}${globalThis.location.search}`)
        : committedRoute;
};
const smsBadgeRefreshes = new WeakMap();

const fetchContextUser = async (userId) => {
    if (!userId) {
        return null;
    }

    const accountId = useAccountStore().contextAccount?.id;

    if (accountId) {
        const accountUser = useUserStore()
            .getUsers(accountId)
            .find((user) => user.id === userId);

        if (accountUser) {
            return accountUser;
        }
    }

    return api.users.show(userId);
};

const fetchContextGroup = async (groupId) => {
    if (!groupId) {
        return null;
    }

    return api.groups.show(groupId, {
        include: ['accounts.sources:id,name'],
    });
};

const fetchContextLead = async (leadId) => {
    if (!leadId) {
        return null;
    }

    try {
        return await api.leads.show(leadId);
    } catch (error) {
        if (!error.response) {
            throw error;
        }

        // 5xx error
        if (error.response.status.toString().startsWith(5)) {
            dispatch('appendNewError', {
                code: '0047',
                display: false,
                error,
                payload: { id },
            });
        }

        if (error.response.status == 404) {
            const mergedId = get(error.response, 'data.errors.mergedId');
            const deletedBy = get(error.response, 'data.errors.deletedBy');
            const currentRoute = getCurrentRoute();

            if (deletedBy) {
                showLeadDeleted(deletedBy);
                router.replace('/');

                return null;
            }

            if (!mergedId) {
                showLeadNotFound();
                router.replace('/');

                return null;
            }

            if (currentRoute.name == 'leads.update' && currentRoute.params.id == leadId) {
                router.replace({
                    name: 'leads.update',
                    params: {
                        id: error.response.data.errors.mergedId,
                    },
                });
            }
        }

        throw error;
    }
};

const redirectAfterLeadFetchError = (error, leadId, globalStore) => {
    if (!error.response) {
        return;
    }

    const mergedId = error.response.data.errors ? error.response.data.errors.mergedId : null;

    if (error.response.status == 404 && mergedId) {
        router.replace({
            name: 'leads.update',
            params: {
                id: mergedId,
            },
        });
    } else if ([403, 404].includes(error.response.status)) {
        router.replace('/');
    } else {
        globalStore.appendNewError({
            code: '0105',
            display: false,
            error,
            payload: { leadId },
        });

        router.replace('/');
    }
};

const fetchWebOrderBadgeCount = async (accountId) => {
    if (!accountId) {
        return null;
    }

    const response = await axios.get(`v1/accounts/${accountId}/web-order-badge-count`);

    return response.data.badgeCount;
};

const fetchContextAccountData = async (
    accountStore,
    userStore,
    accountId,
    bypassCache,
    request
) => {
    const isAccountSwitch =
        accountStore.contextAccount?.id != null &&
        Number(accountStore.contextAccount.id) !== Number(accountId);
    const [accountLoaded, users] = await Promise.all([
        accountStore.fetchContextAccount(accountId, bypassCache, request),
        userStore.fetchUsersByAccountId(accountId, false, request),
        isAccountSwitch ? accountStore.fetchAccounts(true) : Promise.resolve(),
    ]);

    if (
        !accountLoaded ||
        !accountStore.isCurrentContextAccountRequest(request) ||
        Number(accountStore.contextAccount?.id) !== Number(accountId)
    ) {
        return false;
    }

    if (users) {
        await userStore.setContextAccountUsers(users, accountId, request);
    }

    return accountStore.isCurrentContextAccountRequest(request);
};

const fetchSmsBadgeCount = async (accountId) => {
    if (!accountId) {
        return null;
    }

    const response = await axios.get(`v1/auth-user/accounts/${accountId}/sms-badge-count`);

    return response.data.data;
};

export default {
    async setContextUserAction(userId) {
        const userStore = useUserStore();
        const tmpAuthUser = userStore.authUser;

        if (!userId) {
            if (tmpAuthUser.hasSimpleAccess()) {
                await this.setContextUserAction(tmpAuthUser.id);
            } else {
                this.setContextUser(null);
                this.setContextUserId(null);
            }

            return;
        }

        if (userId == this.userId) {
            return;
        }

        wait.start('fetching.contextUser');

        this.setContextUserId(userId);

        try {
            const user = await fetchContextUser(userId);

            this.setContextUser(user);

            await this.setContextAccountAction(user.account_id);
            await this.setContextGroupAction();

            wait.end('fetching.contextUser');
        } catch (error) {
            Sentry.captureException(error);

            wait.end('fetching.contextUser');

            if (tmpAuthUser.hasSimpleAccess()) {
                await this.setContextUserAction(tmpAuthUser.id);
            }
        }
    },
    async setContextGroupAction(groupId) {
        const accountStore = useAccountStore();
        const userStore = useUserStore();

        if (!groupId) {
            this.setContextGroup(null);
            this.setContextGroupId(null);
            return;
        }

        if (groupId == this.groupId) {
            return;
        }

        wait.start('fetching.contextGroup');

        this.setContextGroupId(groupId);

        try {
            const group = await fetchContextGroup(groupId);

            this.setContextGroup(group);

            if (!accountStore.contextAccountId) {
                await this.setContextAccountAction(userStore.authUser.account_id);
            }
            await this.setContextUserAction();

            wait.end('fetching.contextGroup');
        } catch (error) {
            Sentry.captureException(error);

            wait.end('fetching.contextGroup');

            this.setContextGroup(null);
            this.setContextGroupId(null);
        }
    },
    async setContextData(initialRoute = null) {
        const currentRoute = initialRoute ?? getContextRoute();
        const query = parseQuery(currentRoute.query);
        const storage = ls.get('context', {});

        const context = {
            accountId: isValidNumber(query.accountId) ? query.accountId : storage.accountId,
            groupId: isValidNumber(query.groupId) ? query.groupId : storage.groupId,
            userId: isValidNumber(query.userId) ? query.userId : storage.userId,
        };

        this.setContextLead(null);
        this.setContextDivisionId(storage.divisionId);
        this.setContextTeamId(storage.teamId);

        if (currentRoute.name === 'leads.update' && currentRoute.params.id) {
            wait.start('initializing.lead');
            await this.setContextLeadIdAction(currentRoute.params.id);
            wait.end('initializing.lead');
            return;
        }

        if (currentRoute.name === 'accounts.update' && currentRoute.params.id) {
            context.accountId = currentRoute.params.id;
        }

        wait.start('fetching.contextData');

        await this.setContextGroupAction(context.groupId);

        if (!this.groupId && context.userId) {
            await this.setContextUserAction(context.userId);
        }

        const contextAccountId = useAccountStore().contextAccount?.id;
        const requestedAccountChanged =
            isValidNumber(query.accountId) &&
            Number(context.accountId) !== Number(contextAccountId);
        if (!contextAccountId || requestedAccountChanged) {
            if (
                useUserStore().parentAuthUser?.default_account_id &&
                !isValidNumber(query.accountId)
            ) {
                context.accountId = useUserStore().parentAuthUser.default_account_id;
            }

            await this.setContextAccountAction(context.accountId);
        }
        wait.end('fetching.contextData');
    },
    async setContextAccountAction(accountId, bypassCache = false) {
        const accountStore = useAccountStore();
        const userStore = useUserStore();
        const globalStore = useGlobalStore();

        if (!authService.ready()) {
            await authService.waitForUser();
        }

        const authUser = userStore.parentAuthUser;
        let resolvedAccountId = accountId;

        if (!isValidNumber(resolvedAccountId)) {
            const authUserDefaultAccountId = authUser.default_account_id || authUser.account_id;
            resolvedAccountId = authUser.isAdmin() ? Account.DEMO : authUserDefaultAccountId;
        }

        if (!authUser.hasAccessToSeeAccount(resolvedAccountId)) {
            resolvedAccountId = authUser.account_id;
        }

        const request = accountStore.beginContextAccountRequest(resolvedAccountId, true);

        if (request.promise) {
            await request.promise;
            return;
        }

        if (resolvedAccountId == accountStore.contextAccountId) {
            return;
        }

        const loadAccount = async () => {
            try {
                const accountLoaded = await fetchContextAccountData(
                    accountStore,
                    userStore,
                    resolvedAccountId,
                    bypassCache,
                    request
                );

                if (!accountLoaded) {
                    return;
                }

                this.setContextTeamId(null);
                this.setContextLexicon();

                if (authUser.hasAccessTo('dashboards.webOrder')) {
                    fetchWebOrderBadgeCount(resolvedAccountId).then((badgeCount) => {
                        if (accountStore.isCurrentContextAccountRequest(request)) {
                            globalStore.updateWebOrderBadgeCount(badgeCount);
                        }
                    });
                }

                if (
                    accountStore.contextAccount?.hasSmsV2Native() &&
                    authUser.selfOrChildHasNiotext()
                ) {
                    this.refreshSmsBadgeCountAction(resolvedAccountId);
                }
            } catch (error) {
                if (!accountStore.isCurrentContextAccountRequest(request)) {
                    return;
                }

                Sentry.captureException(error);
                console.error('Error fetching context account:', error);
                if (Number(resolvedAccountId) !== Number(authUser.account_id)) {
                    await this.setContextAccountAction(authUser.account_id);
                }
            }
        };

        request.promise = loadAccount();
        try {
            await request.promise;
        } finally {
            request.promise = null;
        }
    },
    async refreshSmsBadgeCountAction(accountId = useAccountStore().contextAccount?.id) {
        if (!accountId) {
            return null;
        }

        const badgeAccountId = Number(accountId);

        if (!smsBadgeRefreshes.has(this)) {
            smsBadgeRefreshes.set(this, new Map());
        }

        const pendingRefreshes = smsBadgeRefreshes.get(this);
        const pendingRefresh = pendingRefreshes.get(badgeAccountId);

        if (pendingRefresh) {
            pendingRefresh.requested = true;
            return pendingRefresh.promise;
        }

        const accountStore = useAccountStore();
        const globalStore = useGlobalStore();
        const refresh = { requested: false, promise: null };

        refresh.promise = (async () => {
            try {
                let badgeCount;

                do {
                    refresh.requested = false;
                    // Events received during a fetch share one trailing refresh so their
                    // latest count is not lost and pending requests are never cancelled.
                    try {
                        // eslint-disable-next-line no-await-in-loop -- Badge refreshes must be sequential
                        badgeCount = await fetchSmsBadgeCount(badgeAccountId);
                    } catch (error) {
                        if (!refresh.requested) {
                            throw error;
                        }

                        // A newer event still needs a refresh after this request fails.
                        continue;
                    }

                    if (Number(accountStore.contextAccount?.id) === badgeAccountId) {
                        globalStore.updateSmsBadgeCount(badgeCount);
                    }
                } while (refresh.requested);

                return badgeCount;
            } catch (error) {
                if (Axios.isCancel(error) || error?.code === 'ERR_CANCELED') {
                    return null;
                }

                throw error;
            } finally {
                pendingRefreshes.delete(badgeAccountId);
            }
        })();

        pendingRefreshes.set(badgeAccountId, refresh);

        return refresh.promise;
    },
    setContextLexicon() {
        const accountStore = useAccountStore();

        const replaceLexicon = (locale, messages) => {
            const localeMessages = i18n.getLocaleMessage(locale);
            localeMessages.lexicon = messages;

            i18n.setLocaleMessage(locale, localeMessages);
        };

        if (accountStore.contextAccount?.isPolestar()) {
            replaceLexicon('fr', polestarLexiconFr);
            replaceLexicon('fr-CA', polestarLexiconFr);
            replaceLexicon('en', polestarLexiconEn);
            replaceLexicon('en-CA', polestarLexiconEn);
        } else if (accountStore.contextAccount?.hasHyundaiSupplier()) {
            replaceLexicon('fr', hyundaiLexiconFr);
            replaceLexicon('fr-CA', hyundaiLexiconFr);
            replaceLexicon('en', hyundaiLexiconEn);
            replaceLexicon('en-CA', hyundaiLexiconEn);
        } else {
            replaceLexicon('fr', defaultLexiconFr);
            replaceLexicon('fr-CA', defaultLexiconFr);
            replaceLexicon('en', defaultLexiconEn);
            replaceLexicon('en-CA', defaultLexiconEn);
        }
    },
    async setContextLeadIdAction(leadId) {
        const accountStore = useAccountStore();
        const globalStore = useGlobalStore();

        if (!leadId) {
            this.setContextLead(null);
            return;
        }

        if (leadId == this.leadId) {
            return;
        }

        wait.start('fetching.lead');

        try {
            const lead = new Lead(await fetchContextLead(leadId));
            this.setContextLead(lead);
            SearchBar.setFromContextLead(lead);

            if (getCurrentRoute().name !== 'calendar') {
                await this.setContextAccountAction(lead.account_id);
            }
        } catch (error) {
            Sentry.captureException(error);
            this.setContextLead(null);

            if (
                error.response?.status === 403 &&
                error.response?.data?.code === 'crm_lite_full_crm_forbidden'
            ) {
                return;
            }

            redirectAfterLeadFetchError(error, leadId, globalStore);

            if (!accountStore.contextAccountId) {
                await this.setContextAccountAction();
            }
        } finally {
            wait.end('fetching.lead');
        }
    },

    setContextDivisionId(divisionId) {
        this.divisionId = parseInt(divisionId, 10) || null;
        updateLocalStorage({ divisionId: this.divisionId });
    },

    setContextLeadId(leadId) {
        this.leadId = parseInt(leadId, 10) || null;
        updateLocalStorage({ leadId: this.leadId });
    },

    setContextLead(lead) {
        this.lead = lead || new Lead();
        this.setContextLeadId(lead?.id);
    },

    setContextTeamId(teamId) {
        const accountStore = useAccountStore();

        const teamExists = accountStore.contextAccount?.teams.some((team) => team.id == teamId);

        if (!teamExists) {
            teamId = null;
        }

        this.teamId = parseInt(teamId, 10) || null;
        updateLocalStorage({ teamId: this.teamId });
    },

    setContextGroupId(groupId) {
        this.groupId = parseInt(groupId, 10) || null;
        updateLocalStorage({ groupId: this.groupId });
    },

    setContextUserId(userId) {
        this.userId = parseInt(userId, 10) || null;
        updateLocalStorage({ userId: this.userId });
    },

    setContextGroup(group) {
        this.group = new Group(group);
    },

    setContextUser(user) {
        this.user = new User(user);
    },

    async validateLeadPhones(leadId) {
        const globalStore = useGlobalStore();

        try {
            const lead = await api.leads.validatePhones(leadId);

            return lead.lead_phones.find((phone) => phone.validated && phone.mobile);
        } catch (error) {
            if (error.response && error.response.status === 403) {
                return null;
            }

            globalStore.appendNewError({
                code: '0006',
                display: true,
                error,
                payload: leadId,
            });
        }

        return null;
    },

    bulkLeadCommunicatingStarted(leads) {
        const startedLeads = leads || [];

        startedLeads.forEach((lead) => this.setCommunicating(lead));

        if (!this.leadId) {
            return;
        }

        const currentLead = startedLeads.find((lead) => lead.id === this.leadId);

        if (!currentLead) {
            return;
        }

        if (useUserStore().authUser?.isSameUser(currentLead.communicating_user?.id)) {
            return;
        }

        this.setPhoneIsEnabled(false);
        this.setEmailIsEnabled(false);
        this.setSmsIsEnabled(false);
    },

    async bulkLeadCommunicatingEnded(leads) {
        const endedLeads = leads || [];

        endedLeads.forEach((lead) => this.removeCommunicating(lead.id));

        if (this.leadId && endedLeads.some((lead) => lead.id === this.leadId)) {
            try {
                await this.checkCommunicating(this.leadId);
            } catch {
                // No-op
            }
        }
    },

    async checkCommunicating(leadId) {
        if (!leadId) {
            leadId = this.leadId;
        }

        if (!leadId) {
            return;
        }

        if (leadId == this.leadId) {
            this.setPhoneIsEnabled(false);
            this.setEmailIsEnabled(false);
            this.setSmsIsEnabled(false);
        }

        const response = await api.leads.checkCommunicating(leadId);

        this.applyCommunicatingResult(leadId, response);
    },

    // Set the active context lead and apply its communicating result in one
    // step. The lead must be set first because `applyCommunicatingResult` only
    // writes the enable flags when the lead is the current context lead; owning
    // that order here keeps callers from having to sequence the two correctly.
    hydrateActiveLead({ lead, communicating }) {
        this.setContextLead(lead);
        this.applyCommunicatingResult(lead.id, communicating);
    },

    // Apply a check-communicating result to the store: track/untrack the lead in
    // `communicatingLeads`, and write the enable flags only when `leadId` is the
    // current context lead (other leads must not drive the active enablement).
    // Extracted so callers that already hold the result — e.g. a combined
    // fetch — can apply it without re-hitting the API.
    applyCommunicatingResult(leadId, response) {
        if (response.isCommunicating) {
            this.setCommunicating(response.lead);
        } else {
            this.removeCommunicating(leadId);
        }

        if (leadId == this.leadId) {
            this.setPhoneIsEnabled(response.phoneIsEnabled);
            this.setEmailIsEnabled(response.emailIsEnabled);
            this.setSmsIsEnabled(response.smsIsEnabled);
        }
    },

    setCommunicating(lead) {
        if (
            !this.communicatingLeads.some((communicatingLead) => communicatingLead.id === lead.id)
        ) {
            this.communicatingLeads.push(lead);
        }
    },

    removeCommunicating(leadId) {
        this.communicatingLeads = this.communicatingLeads.filter(
            (communicatingLead) => communicatingLead.id !== leadId
        );
    },

    setPhoneIsEnabled(value) {
        this.phoneIsEnabled = value;
    },

    setEmailIsEnabled(value) {
        this.emailIsEnabled = value;
    },

    setSmsIsEnabled(value) {
        this.smsIsEnabled = value;
    },

    setCurrentMergeAction(mergeAction) {
        this.currentMergeAction = mergeAction;
    },

    setMergeManualCheckDuplicate(value) {
        this.mergeManualCheckDuplicate = value;
    },

    setHideSensitiveInfoMode(value) {
        this.hideSensitiveInfoMode = value;
    },

    toggleMergeManualCheckDuplicate() {
        this.mergeManualCheckDuplicate = !this.mergeManualCheckDuplicate;
    },

    toggleHideSensitiveInfoMode() {
        this.hideSensitiveInfoMode = !this.hideSensitiveInfoMode;
    },
};
